import Vue from 'vue'
import Vuex from 'vuex'
import axios, { ApiResponse } from "@/plugins/axios";
import Swal from "sweetalert2-khonik"; // В store используем прямой импорт
import domainModule from "@/store/domain";
import smtpModule from "@/store/smtp";
import smsModule from "@/store/sms";
import printerModule from "@/store/printer";

Vue.use(Vuex)


export interface AppState {
    routes: any[]
    jwt?: string
    user?: IUser
    company?: ICompany
    branch?: IBranch
    attachedCustomer?: any
    detachedCustomer?: any
    accessLevels: any[]
    need_branch: boolean
    custom_host: boolean
    available_branch_select: boolean
    permissions: any
    new_order_requests_count: number
    maxBotConfigured: boolean
    forcedMobileMode: boolean
    impersonation: {
        isImpersonating: boolean
        impersonator?: { id: number; name: string; email: string } | null
    }
}

export type EntityType = "entity" | "entrepreneur" | "self_employed";

export interface IShortSettings {
    id: number
    brand_name: string
    logo: string
    primary_color: string
    tax_system?: string | null
    vat_rate?: string | null
}

export interface ICompany {
    id: number
    access_level: number
    name: string
    slug: string
    subscription?: ISubscription
    settings?: IShortSettings
    users?: IUser[]
    crm_smtp_disabled?: boolean
    has_own_smtp?: boolean
}

export interface IBranch {
    id: number
    name: string
    access_level: number
}

export interface ISubscription {
    amount: number
    expires_at: string
    users_count: number
    modules: IModule[]
}

export interface IModule {
    id: number
    key: string
    name: string
    description: string
    price: number
    icon?: string
    color?: string
}

export interface ICreateCompanyRequest {
    name: string
}

export interface IPlan {
    id: number
    is_default: boolean
    name: string
    users_count: number
    duration: number
    price: number
}

export interface IUser {
    id: number
    customer_id: number
    name: string
    surname: string
    father_name: string // new
    email: string
    email_verified_at: string
    phone: string
    telegram: string
    balance: number
    created_at: string
    last_activity_date: string
    tg_username: string
    tg_channel_id: string
    max_user_id?: string | null
    max_username?: string | null
    company_context_id: number
    branch_context_id: number | null
    is_admin: boolean
    is_admin_stats?: boolean
    novofon_employee_id?: string | null
    novofon_virtual_phone?: string | null
}

export interface ISettings {
    entity_type: EntityType
    brand_name: string
    legal_name: string
    legal_full_name: string
    legal_statement: string
    ogrn: string
    inn: string
    kpp: string // new
    okpo: string // new
    okved: string // new
    city: string
    address: string
    legal_address: string // new
    ceo_name: string // new
    ceo_name_genitive: string // new
    ceo_title_genitive: string // new
    bank: string
    rs: string
    ks: string
    bik: string
    phone: string
    phone_alt?: string
    whatsapp?: string
    email: string
    website: string | undefined
    max_url?: string
    max_manager_url?: string
    youtube?: string
    vk?: string
    rutube?: string
    dzen?: string
    telegram_channel?: string
    telegram_manager?: string
    instagram?: string
    notification_time: string
    logo: string
    domain: string
    domain_verified: boolean
    mail_footer: string
    order_notification_email_header_html?: string | null
    order_notification_email_footer_html?: string | null
    order_notification_email_include_layout?: boolean
    /** Второй цвет градиента шапки (#RRGGBB) или пусто — #764ba2 */
    order_notification_email_gradient_end?: string | null
    /** Первый цвет градиента шапки (#RRGGBB) или пусто — #667eea */
    order_notification_email_gradient_start?: string | null
    director_sign: string
    stamp: string
    primary_color: string //new
    write_off_method?: 'fifo' | 'lifo'
    /** Если задано (1–366) — раз в день push о строках резерва склад ГП старше этого числа дней под нарядами */
    goods_reserve_alert_days?: number | null
    tax_system?: string
    vat_rate?: string
}

export type AccountType = "cash" | "card" | "account" | "acquiring"

export interface IAccount {
    type: AccountType // new
    selected: boolean // new
    name: string
    balance: number
    expected_income: number
    prepayment: number
    details: string
}

export const CompanyEntityTypes = [
    {
        key: "entity", value: "Юридическое лицо",
    },
    {
        key: "entrepreneur", value: "Индивидуальный предприниматель (ИП)",
    },
    {
        key: "self_employed", value: "Самозанятый / физ. лицо",
    },
]

/** Лимиты справочника юрлиц (синхронно с CompanyLegalEntity::MAX_*) */
export const COMPANY_LEGAL_ENTITIES_DIRECTORY_MAX = 5
export const COMPANY_LEGAL_ENTITIES_TOTAL_MAX = 6

/** Краткие подписи ОПФ для таблиц (как в backend Settings::EntityTypes) */
export const CompanyEntityTypeTableLabels: Record<string, string> = {
    entity: 'Юридическое лицо',
    entrepreneur: 'ИП',
    self_employed: 'Самозанятый',
}

/** Меню для клиентов (access_level === 0) — только нужные разделы */
const clientRoutes = [
    { title: 'Главная',           icon: 'mdi-view-dashboard',       to: '/customer-home' },
    { title: 'Мои заказы',        icon: 'mdi-cart-outline',          to: '/customer-orders' },
    { title: 'Документы / Счета', icon: 'mdi-file-document-outline', to: '/customer-documents' },
    { title: 'Платежи',           icon: 'mdi-cash',                  to: '/customer-payments' },
    { title: 'Профиль',           icon: 'mdi-account',               to: '/customer-profile' },
    { title: 'Поддержка',         icon: 'mdi-rocket',                to: '/customer-support' },
];

const isRouteVisible = (r: any, permissions: any, user: any, company?: any) => {
    if (r.children) {
        r.children = r.children.filter((r: any) => isRouteVisible(r, permissions, user, company))
        return r.children.length > 0;
    } else {
        // Новогодний маршрут всегда виден
        if (r.isNewYear) {
            return true;
        }
        // Платный модуль «Мониторинг принтеров» (очередь печати и мониторинг)
        if (r.to === '/print-queue' || r.to === '/monitoring') {
            if (!company || company.access_level <= 0) {
                return false;
            }
            const subscription = company?.subscription;
            if (!subscription?.modules) {
                return false;
            }
            const hasModule = !!subscription.modules.find((m: IModule) => m.key === 'printer_monitoring');
            if (!hasModule) {
                return false;
            }
            const permission = permissions[r.key];
            return !!(permission && permission['viewAny']);
        }
        // Платный модуль «Калькулятор себестоимости»
        if (r.to === '/cost-calculators') {
            if (!company || company.access_level <= 0) {
                return false;
            }
            const subscription = company?.subscription;
            if (!subscription?.modules) {
                return false;
            }
            return !!subscription.modules.find((m: IModule) => m.key === 'cost_calculator');
        }
        // Проверяем requiresAdminStats перед проверкой permissions
        if (r.requiresAdminStats) {
            return user && user.is_admin_stats === true;
        }
        // Проверяем requiresCompanyAdmin (только админ компании)
        if (r.requiresCompanyAdmin) {
            if (!company || company.access_level <= 1) {
                return false;
            }
            // Для пункта меню "Репосты" дополнительно проверяем наличие модуля в подписке
            if (r.to === '/reposts') {
                const subscription = company?.subscription;
                if (!subscription || !subscription.modules) {
                    return false;
                }
                const repostsModule = subscription.modules.find((m: IModule) => m.key === 'reposts');
                return !!repostsModule;
            }
            if (r.to === '/radio-versta') {
                const subscription = company?.subscription;
                if (!subscription?.modules) {
                    return false;
                }
                if (permissions?.radio_versta && !permissions.radio_versta.viewAny) {
                    return false;
                }
                return !!subscription.modules.find((m: IModule) => m.key === 'radio_versta');
            }
            if (r.to === '/company-legal-entities') {
                const subscription = company?.subscription;
                if (!subscription?.modules) {
                    return false;
                }
                return !!subscription.modules.find((m: IModule) => m.key === 'company_legal_entities');
            }
            return true;
        }
        // Проверяем requiresHigherThanCustomer (только для пользователей выше CUSTOMER)
        if (r.requiresHigherThanCustomer) {
            // Проверяем, что уровень доступа выше CUSTOMER (0)
            // EMPLOYEE = 1, ADMIN = 2
            return company && company.access_level > 0;
        }
        if (!r.key) return true;
        const permission = permissions[r.key];
        // Проверяем существование permission перед обращением к viewAny
        return permission && permission["viewAny"];
    }
}

export default new Vuex.Store({
    state: <AppState>{
        routes: [],
        jwt: localStorage.getItem('access_token'),
        user: undefined as IUser | undefined,
        company: undefined as ICompany | undefined,
        branch: undefined as ICompany | undefined,
        attachedCustomer: undefined,
        detachedCustomer: undefined,
        accessLevels: [
            { key: 0, value: "Клиент" },
            { key: 1, value: "Сотрудник" },
            { key: 2, value: "Администратор" },
        ],
        need_branch: false,
        custom_host: false,
        available_branch_select: false,
        permissions: {},
        new_order_requests_count: 0,
        maxBotConfigured: false,
        forcedMobileMode: false,
        impersonation: {
            isImpersonating: false,
            impersonator: null,
        },
    },
    getters: {
        routes: (state) => {
            return [
                { title: 'Главная', icon: 'mdi-view-dashboard', to: '/', },
                { title: 'Поиск', icon: 'mdi-magnify', to: '/search' },
                { title: 'Мониторинг', icon: 'mdi-monitor-dashboard', to: '/monitoring', key: 'monitoring' },
                { title: 'Очередь печати', icon: 'mdi-playlist-play', to: '/print-queue', key: 'printQueue' },
                { title: 'Клиенты', icon: 'mdi-toolbox', to: '/customers', key: 'customer' },
                { title: 'Заявки', icon: 'mdi-text-box-multiple-outline', to: '/requests', key: 'request', badge: state.new_order_requests_count },
                { title: 'Наряд-заказы', icon: 'mdi-cart-outline', to: '/orders', key: 'order' },
                { title: 'Деньги', icon: 'mdi-cash', to: '/money', key: 'payment' },
                {
                    title: 'Склад',
                    icon: 'mdi-store',
                    children: [
                        { title: 'Материалы', icon: 'mdi-package-variant', to: '/stock', key: 'part' },
                        { title: 'Товары', icon: 'mdi-cube', to: '/stock-goods', key: 'goodsItem' },
                        { title: 'Аналитика', icon: 'mdi-chart-line', to: '/stock-goods/analytics', key: 'goods-analytics' },
                    ],
                },
                {
                    title: "Справочники", icon: "mdi-palette-swatch-variant",
                    children: [
                        {
                            title: 'Филиалы',
                            icon: 'mdi-source-branch',
                            to: '/branches',
                            key: 'branch'
                        },
                        {
                            title: 'Адреса доставки',
                            icon: 'mdi-truck-delivery-outline',
                            to: '/delivery-addresses',
                            key: 'deliveryAddress'
                        },
                        { title: 'Счета', icon: 'mdi-wallet-plus', to: '/accounts', key: 'account' },
                        {
                            title: 'Мои юр. лица',
                            icon: 'mdi-domain',
                            to: '/company-legal-entities',
                            requiresCompanyAdmin: true,
                        },
                        { title: 'Сотрудники', icon: 'mdi-account-multiple', to: '/users', key: 'user' },
                        {
                            title: 'Безопасность',
                            icon: 'mdi-shield-lock',
                            to: '/security',
                            requiresCompanyAdmin: true,
                        },
                        {
                            title: 'Категории расходов',
                            icon: 'mdi-credit-card-settings-outline',
                            to: '/payment-purposes',
                            key: 'paymentPurpose'
                        },
                        {
                            title: 'Источники лидов',
                            icon: 'mdi-transfer-down',
                            to: '/lead-sources',
                            key: 'leadSource'
                        },
                        {
                            title: 'Маркетплейсы',
                            icon: 'mdi-storefront-outline',
                            to: '/goods-marketplaces',
                            key: 'goodsMarketplace'
                        },
                        {
                            title: 'Категории товаров',
                            icon: 'mdi-folder-outline',
                            to: '/goods-categories',
                            key: 'goodsCategory'
                        },
                        {
                            title: 'Принтеры',
                            icon: 'mdi-printer-3d',
                            to: '/printers',
                            key: 'printer'
                        },
                        {
                            title: 'Ценообразование',
                            icon: 'mdi-cash-multiple',
                            to: '/pricing',
                            key: 'pricing'
                        },
                        {
                            title: 'Калькулятор себестоимости',
                            icon: 'mdi-calculator-variant',
                            to: '/cost-calculators',
                            requiresHigherThanCustomer: true,
                        },
                    ],
                },
                {
                    title: "Инструменты", icon: "mdi-pliers",
                    children: [
                        { title: 'Звонки', icon: 'mdi-phone-log', to: '/novofon', key: 'novofon' },
                        { title: 'Точка Банк', icon: 'mdi-bank', to: '/tochka-bank', key: 'tochka' },
                        { title: 'Ozon Bank', icon: 'mdi-bank-outline', to: '/ozon-bank', key: 'ozon' },
                        { title: 'Дедлайны', icon: 'mdi-call-split', to: '/deadline', key: 'deadline' },
                        { title: 'Рег. платежи', icon: 'mdi-cash-clock', to: '/regular-payments', key: 'regularPayment' },
                        { title: 'Задачи', icon: 'mdi-calendar-check', to: '/tasks', key: 'task' },
                        { title: 'Рассылки', icon: 'mdi-email-arrow-right-outline', to: '/newsletters', key: 'newsletter' },
                        { title: 'Воронка продаж', icon: 'mdi-trending-up', to: '/funnel', key: 'funnel' },
                        { title: 'Репосты', icon: 'mdi-share-variant', to: '/reposts', requiresCompanyAdmin: true },
                        { title: 'РадиоВерста', icon: 'mdi-radio', to: '/radio-versta', key: 'radio_versta', requiresCompanyAdmin: true },
                        { title: 'Лента событий', icon: 'mdi-history', to: '/activity-feed', requiresHigherThanCustomer: true },
                        { title: 'Статистика CRM', icon: 'mdi-chart-box-outline', to: '/platform-stats', requiresAdminStats: true },
                        { title: 'Статистика использования', icon: 'mdi-chart-line', to: '/usage-statistics', requiresAdminStats: true },
                        { title: 'Управление ботом', icon: 'mdi-robot', to: '/bot-management', requiresAdminStats: true },
                    ],
                },
                {
                    title: "Шаблоны", icon: "mdi-clipboard-list-outline", children: [
                        { title: 'Договора', icon: 'mdi-file-sign', to: '/contracts', key: 'contract' },
                        { title: 'Сметы', icon: 'mdi-clipboard-list', to: '/estimates', key: 'estimate' },
                    ]
                },

                { title: 'Настройки', icon: 'mdi-cog', to: '/settings', key: "settings" },
                { title: 'Подписка', icon: 'mdi-shopping', to: '/subscription', key: 'subscription' },
                { title: 'Профиль', icon: 'mdi-account', to: '/profile', },
                { title: 'Поддержка', icon: 'mdi-rocket', to: '/support', },
            ]
        },
        jwt: state => state.jwt,
        user: state => state.user,
        company: state => state.company,
        visibleRoutes: (state, getters) => {
            const user = <any>state.user;
            if (!user) return [];
            const company = <ICompany | undefined>state.company;
            if (!company) return [];
            // Клиент видит сокращённое меню
            if (company.access_level === 0) {
                return [...clientRoutes];
            }
            return getters.routes.filter((r: any) => isRouteVisible(r, state.permissions, user, company)).map((r: any) => {
                r.children = r.children?.filter((ch: any) => !ch.permission || ch.permission <= company.access_level);
                return r;
            });
        },
        isCustomer: state => {
            return state.company && state.company.access_level === 0;
        },
        isModerator: state => {
            return state.company && state.company.access_level > 0;
        },
        isAdmin: state => {
            return state.company && state.company.access_level > 1;
        },
        accessLevels: state => state.accessLevels,
        needBranch: state => state.need_branch,
        onCustomHost: state => state.custom_host,
        availableBranchSelect: state => state.available_branch_select,
        branch: state => state.branch,
        permissions: state => state.permissions,
        modules: state => state.company?.subscription?.modules || [],
        moduleEnabled: (state, getters) => (key: string) => {
            return !!getters.modules.find((m: IModule) => m.key === key)
        },
        new_order_requests_count: state => state.new_order_requests_count,
        maxBotConfigured: state => state.maxBotConfigured,
        forcedMobileMode: state => state.forcedMobileMode,
        impersonation: state => state.impersonation,
        isImpersonating: state => state.impersonation.isImpersonating,
    },
    mutations: {
        setToken(state, token) {
            state.jwt = token;
            localStorage.setItem('access_token', token)
        },
        logout(state) {
            state.jwt = undefined;
            state.user = undefined;
            state.company = undefined;
            state.branch = undefined;
            state.impersonation = { isImpersonating: false, impersonator: null };
            localStorage.removeItem('access_token')
        },
        setUser(state, user) {
            state.user = user;
        },
        needBranch(state, value) {
            state.need_branch = value;
        },
        setCustomHost(state, value) {
            state.custom_host = value;
        },
        availableBranchSelect(state, value) {
            state.available_branch_select = value;
        },
        setPermissions(state, value) {
            state.permissions = value;
        },
        attachToNewsletter(state, user) {
            state.attachedCustomer = user;
        },
        detachFromNewsletter(state, user) {
            state.detachedCustomer = user;
        },
        switchCompanyContext(state, company: ICompany) {
            state.company = company;
            state.branch = undefined;
            if (state.user) {
                state.user.company_context_id = company.id;
                state.user.branch_context_id = null;
            }
        },
        switchBranchContext(state, branch: IBranch) {
            state.branch = branch;
            state.need_branch = false;
            if (state.user) {
                state.user.branch_context_id = branch.id;
            }
        },
        /** Только для отображения: когда branch_context_id не задан, используем филиал по умолчанию компании */
        setBranchDisplay(state, branch: IBranch | undefined) {
            state.branch = branch;
        },
        setOrderRequestsCount(state, count: number) {
            state.new_order_requests_count = count;
        },
        setMaxBotConfigured(state, value: boolean) {
            state.maxBotConfigured = !!value;
        },
        toggleForcedMobileMode(state) {
            state.forcedMobileMode = !state.forcedMobileMode;
        },
        setImpersonationState(state, payload: { isImpersonating: boolean; impersonator?: { id: number; name: string; email: string } | null }) {
            state.impersonation = {
                isImpersonating: !!payload?.isImpersonating,
                impersonator: payload?.impersonator || null,
            };
        }
    },
    actions: {
        async getAuthUser({ commit }) {
            // Получаем device_id из localStorage (если есть)
            const deviceId = localStorage.getItem('device_id');
            
            // Формируем URL с device_id (если есть)
            const url = deviceId ? `auth/me?device_id=${deviceId}` : `auth/me`;
            
            const body = <any>await axios.get(url);
            const user = body.user;
            commit('setUser', body.user)
            commit('needBranch', body.need_branch)
            commit('availableBranchSelect', body.available_branch_select)
            commit('setPermissions', body.permissions)
            commit('setCustomHost', body.custom_host)
            commit('setOrderRequestsCount', body.new_order_requests_count)
            commit('setMaxBotConfigured', !!body.max_bot_configured)
            commit('setImpersonationState', {
                isImpersonating: !!body.is_impersonating,
                impersonator: body.impersonator || null,
            })

            if (user.context_company) {
                commit('switchCompanyContext', user.context_company)
            }
            if (user.context_branch) {
                commit('switchBranchContext', user.context_branch)
            } else if (body.default_branch) {
                commit('setBranchDisplay', body.default_branch)
            } else {
                commit('setBranchDisplay', undefined)
            }

            // Возвращаем весь body, включая push_connected_on_device
            return body;
        },
        LOGOUT({ state, commit }) {
            // Best-effort server revoke; always clear local state
            return axios.post('auth/logout').catch(() => null).finally(() => {
                commit('logout')
            })
        },
        async switchCompanyContext({ state, commit }, companyID: number) {
            const { company } = await axios.post<any, ApiResponse>(`auth/set-context`, {
                company_id: companyID
            })
            commit('switchCompanyContext', company);
        },
        async switchBranchContext({ state, commit }, branchID: number) {
            const { branch } = await axios.post<any, ApiResponse>(`auth/set-branch-context`, {
                branch_id: branchID
            })
            commit('switchBranchContext', branch);
        },

        async createCompany({ }, request: ICreateCompanyRequest): Promise<ICompany> {
            const { company } = await axios.post<any, ApiResponse>(`companies`, request);
            return company;
        },
        async sendNewsletter({ }, newsletter: any) {
            const result = await Swal.fire({
                title: "Подтверждаете отправку рассылки?",
                showDenyButton: false,
                showCancelButton: true,
                cancelButtonText: 'Отменить',
                showCloseButton: false,
                showConfirmButton: true,
                confirmButtonText: 'Подтверждаю',

            });

            if (result.isConfirmed) {
                try {
                    await axios.post(`newsletters/${newsletter.id}/send`);
                } catch (err: any) {
                    Swal.fire({
                        icon: "error",
                        title: "Отправка недоступна",
                        text: err.body.message,
                    });
                    throw Error("Отправка недоступна")
                }
            }
        },
        reduceNewOrderRequestsCount({ state }) {
            state.new_order_requests_count--;
            if (state.new_order_requests_count <= 0) {
                state.new_order_requests_count = 0;
            }
        },
        increaseNewOrderRequestsCount({ state }) {
            state.new_order_requests_count++;
        },
      
        toggleForcedMobileMode({ commit }) {
            commit('toggleForcedMobileMode');
        },
        async startCustomerImpersonation({ commit, dispatch }, customerId: number) {
            const body = await axios.post<any, ApiResponse>(`auth/start-customer-impersonation`, { customer_id: customerId });
            commit('setToken', body.access_token);
            await dispatch('getAuthUser');
            return body;
        },
        async stopCustomerImpersonation({ commit, dispatch }) {
            const body = await axios.post<any, ApiResponse>(`auth/stop-customer-impersonation`);
            commit('setToken', body.access_token);
            await dispatch('getAuthUser');
            return body;
        },
        
    },
    modules: {
        domain: domainModule,
        smtp: smtpModule,
        sms: smsModule,
        printers: printerModule
    },
})
